import { useEffect, useState } from 'react'
import type { Announcement } from 'shared'
import { api } from '../utils/api'

const POLL_INTERVAL_MS = 5 * 60_000
const CHANGED_EVENT = 'announcements-changed'
/** Coming back to a tab does not ask again within this time. */
const MIN_GAP_MS = 60_000

/** Called after an admin publishes or withdraws one, so the bar on that same page follows at once. */
export function notifyAnnouncementsChanged() {
  window.dispatchEvent(new Event(CHANGED_EVENT))
}

function readDismissed(key: string): string[] {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(key) ?? '[]')
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : []
  } catch {
    return []
  }
}

function writeDismissed(key: string, ids: string[]) {
  try {
    localStorage.setItem(key, JSON.stringify(ids))
  } catch {
    // Blocked storage: the bar comes back on the next load.
  }
}

/**
 * The announcements to show right now. The ones this person closed stay closed
 * (kept in the browser, per account) until the announcement is gone; an
 * announcement that cannot be closed is never filtered out.
 */
export function useAnnouncements(userId: string | undefined): {
  announcements: Announcement[]
  dismiss: (id: string) => void
} {
  const key = `announcements-dismissed:${userId}`
  const [active, setActive] = useState<Announcement[]>([])
  const [dismissed, setDismissed] = useState<string[]>([])

  useEffect(() => {
    if (!userId) return
    // Another account's list is not this one's, even for the moment before the answer arrives.
    setActive((previous) => (previous.length ? [] : previous))
    setDismissed(readDismissed(key))

    let current = true
    let lastFetch = 0
    const refresh = () => {
      lastFetch = Date.now()
      api
        .get<{ data: Announcement[] }>('/api/announcements/active')
        .then((response) => {
          if (!current) return
          // The same list is the same state: no render every five minutes for nothing.
          setActive((previous) =>
            JSON.stringify(previous) === JSON.stringify(response.data) ? previous : response.data,
          )
        })
        // A failed poll is not worth surfacing: the next one retries.
        .catch(() => undefined)
    }
    // A hidden tab does not poll, and a tab that comes back asks only if it has been a while.
    const onTick = () => {
      if (document.visibilityState === 'visible') refresh()
    }
    const onVisible = () => {
      if (document.visibilityState === 'visible' && Date.now() - lastFetch > MIN_GAP_MS) refresh()
    }

    refresh()
    const interval = setInterval(onTick, POLL_INTERVAL_MS)
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener(CHANGED_EVENT, refresh)
    return () => {
      current = false
      clearInterval(interval)
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener(CHANGED_EVENT, refresh)
    }
  }, [key, userId])

  const dismiss = (id: string) => {
    // Merged with what is stored, not with this tab's copy: another tab may have closed one too.
    const next = [...new Set([...readDismissed(key), id])]
    writeDismissed(key, next)
    setDismissed(next)
  }

  return {
    announcements: active.filter((a) => !a.dismissible || !dismissed.includes(a.id)),
    dismiss,
  }
}
